// useAuth.ts — Authentication hook for the cooking site.
//
// Auth is an authz.lab0.us session held in an HttpOnly cookie that the Go
// webserver sets after the OIDC round-trip (lib/oidcrp). The browser never
// handles a token: /api/me reports the current identity from the cookie, and
// signing in is a redirect to the server-side /api/oidc/login.
import { useState, useEffect, useCallback } from 'react'

export interface AuthState {
    email: string
    name: string
    /** Avatar URL from the authz profile; empty when the issuer has none. */
    photoUrl: string
    roles: string[]
    scopes: string[]
    loading: boolean
    error: string
    authenticated: boolean
}

// useAuth reports the current user, or an unauthenticated state when no valid
// session cookie is present.
export function useAuth(): AuthState {
    const [state, setState] = useState<AuthState>({
        email: '', name: '', photoUrl: '', roles: [], scopes: [], loading: true, error: '', authenticated: false,
    })

    const fetchMe = useCallback(async () => {
        try {
            const res = await fetch('/api/me', { credentials: 'include' })
            if (res.status === 401 || res.status === 403) {
                setState(prev => ({ ...prev, loading: false, authenticated: false }))
                return
            }
            if (!res.ok) throw new Error('Failed to load profile')
            const data = await res.json()
            setState({
                email: data.email || '',
                name: data.name || '',
                // Server-side /api/me follows JSON snake_case, matching the
                // shared lib/web useAuth; accept either spelling.
                photoUrl: data.photo_url || data.photoUrl || '',
                roles: data.roles || [],
                scopes: data.scopes || [],
                loading: false,
                error: '',
                authenticated: true,
            })
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to load profile'
            setState(prev => ({ ...prev, loading: false, error: msg, authenticated: false }))
        }
    }, [])

    useEffect(() => { fetchMe() }, [fetchMe])

    return state
}

// redirectToLogin hands off to the server-side OIDC flow, which bounces to
// authz and returns to the current path.
//
// The current path is deliberately NOT used when it is /login: the callback
// would land back on the login route, which redirects again on mount, and the
// browser bounces between authz and here forever. Anything else returns to
// where the user was.
export function redirectToLogin(): void {
    const path = window.location.pathname + window.location.search
    const returnTo = window.location.pathname.startsWith('/login') ? '/' : path
    window.location.href = `/api/oidc/login?return_to=${encodeURIComponent(returnTo)}`
}

// logout clears the session cookie and reloads.
export function logout(): void {
    window.location.href = '/api/oidc/logout'
}

// apiFetch makes a same-origin API request carrying the session cookie.
export async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
    return fetch(path, { ...options, credentials: 'include' })
}
